import { Gift } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { LogoImg } from "@/components/LogoImg";
import { backdropStyle, brandAsset } from "@/lib/brand";

export function BrandBackdrop({ brand, fixed = true }) {
  const style = backdropStyle(brand);
  if (!style) return null;
  return <div aria-hidden="true" className={`pointer-events-none ${fixed ? "fixed" : "absolute"} inset-0 -z-10`} style={style} data-testid="brand-backdrop" />;
}

export function BrandStrip({ brand, name }) {
  return (
    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-white/10 px-3 py-2 backdrop-blur-xl" style={{ background: `linear-gradient(90deg, ${brand.primary}40, rgba(5,7,13,0.5))` }} data-testid="brand-strip">
      {brand.logo_url && <LogoImg src={brandAsset(brand.logo_url)} fit={brand.logo_fit} alt={`Logo ${name}`} className="h-9 w-9 shrink-0 rounded-xl" />}
      <div className="min-w-0">
        <p className="truncate font-display text-lg font-extrabold uppercase leading-none" data-testid="brand-strip-name">{name}</p>
        {brand.tagline && <p className="truncate text-[11px] text-white/75">{brand.tagline}</p>}
      </div>
    </div>
  );
}

export function BrandLogo({ brand, name, size = 32 }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2.5" data-testid="brand-app-logo">
      {brand.logo_url ? <LogoImg src={brandAsset(brand.logo_url)} fit={brand.logo_fit} alt={`Logo ${name}`} style={{ width: size, height: size }} className="shrink-0 rounded-lg" />
        : <span style={{ width: size, height: size, background: brand.primary }} className="shrink-0 rounded-lg" />}
      <span className="min-w-0 leading-none">
        <span className="line-clamp-2 max-w-[150px] break-words font-display text-base font-extrabold uppercase leading-[1.05] sm:max-w-[170px]">{name}</span>
        <span className="block text-[10px] font-semibold text-white/55">avec Ma Petite Ligue</span>
      </span>
    </span>
  );
}

export function BrandRewards({ brand, name }) {
  if (!brand.rewards?.length) return null;
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-black/25 p-3" data-testid="brand-rewards">
      <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--brand-accent)" }}><Gift size={14} /> À gagner</p>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
        {brand.rewards.map((r, i) => (
          <li key={i} className="flex gap-3 rounded-xl bg-white/[0.04] p-2.5" data-testid={`brand-reward-${i}`}>
            {r.place && <span className="mpl-place shrink-0 self-start rounded-lg border-2 border-transparent px-2 py-1 text-[11px] font-extrabold uppercase" style={{ background: "var(--brand-button)", color: "var(--brand-on-button)" }}>{r.place}</span>}
            <span className="min-w-0"><span className="block text-sm font-bold">{r.title}</span>{r.detail && <span className="block text-xs text-white/70">{r.detail}</span>}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-white/50">Offert par {name}. Aucun argent ne passe par Ma Petite Ligue.</p>
    </div>
  );
}

export function BrandHero({ brand, name }) {
  if (!brand) return null;
  return (
    <div className="mpl-rise relative mb-4 overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl" style={{ background: `linear-gradient(120deg, ${brand.primary}33, rgba(5,7,13,0.55) 60%)` }} data-testid="brand-hero">
      {brand.banner_url && (
        <div className="relative aspect-[5/2] w-full sm:aspect-[3/1]" data-testid="brand-hero-banner">
          <LogoImg src={brandAsset(brand.banner_url)} fit={brand.banner_fit} alt={`Équipe ${name}`} className="!absolute inset-0 h-full w-full" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,0) 45%, rgba(5,7,13,0.75) 100%)" }} />
          {brand.banner_overlay?.opacity > 0 && <div className="absolute inset-0" style={{ background: brand.banner_overlay.color, opacity: brand.banner_overlay.opacity / 100 }} data-testid="brand-hero-overlay" />}
        </div>
      )}
      <div className="absolute inset-x-0 top-0 z-[1] h-1.5" style={{ background: `linear-gradient(90deg, ${brand.primary} 65%, ${brand.secondary} 65%)` }} />
      <div className={`brand-hero-body relative p-5 sm:p-6 ${brand.banner_url ? "-mt-6 pt-0 sm:-mt-8 sm:pt-0" : ""}`}>
      <div className="flex items-end gap-4">
        {brand.logo_url && <LogoImg src={brandAsset(brand.logo_url)} fit={brand.logo_fit} alt={`Logo ${name}`} className="h-16 w-16 shrink-0 rounded-2xl shadow-lg sm:h-20 sm:w-20" testId="brand-hero-logo" />}
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "var(--brand-accent)" }}>Ligue officielle</p>
          <h1 className="font-display text-3xl font-extrabold uppercase leading-none [overflow-wrap:anywhere] sm:text-4xl" style={{ color: "var(--brand-hero-text, inherit)" }} data-testid="league-name">{name} <VerifiedBadge show={brand.verified} size={26} /></h1>
          {brand.tagline && <p className="mt-1 text-sm font-semibold text-white/85" data-testid="brand-hero-tagline">{brand.tagline}</p>}
        </div>
      </div>
      {brand.welcome && <p className="mt-4 rounded-2xl bg-black/25 p-3 text-sm text-white/85" data-testid="brand-hero-welcome">{brand.welcome}</p>}
      <BrandRewards brand={brand} name={name} />
      </div>
    </div>
  );
}
